import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
import { Times } from '@styled-icons/fa-solid/Times';
import { Users } from '@styled-icons/fa-solid/Users';
import { Lock } from '@styled-icons/fa-solid/Lock';
import { Archive } from '@styled-icons/fa-solid/Archive';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';
import { device } from '../../constants/device-sizes';
import { renderActiveUsersWithColors } from '../../utils/renderActiveUsers.jsx';
import { isChatScapeMode } from '../../hooks/useSiteConfig';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle
} from './shared/ModalBase';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  color: var(--clr-sec);
  opacity: 0.7;
  z-index: 10;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
    transform: scale(1.1);
  }
`;

const RoomsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
`;

const RoomCard = styled.div`
  background-color: var(--clr-prim);
  border: 2px solid
    ${(props) => {
      if (props.isCurrentRoom) return 'var(--clr-green)';
      if (!props.accessible) return 'rgba(128, 128, 128, 0.5)';
      return 'var(--clr-tert)';
    }};
  border-radius: 8px;
  padding: 1rem;
  cursor: ${(props) => {
    if (props.isCurrentRoom) return 'not-allowed';
    if (!props.accessible) return 'not-allowed';
    return 'pointer';
  }};
  transition: all 0.2s ease;
  position: relative;
  opacity: ${(props) => {
    if (props.isCurrentRoom) return 0.8;
    if (!props.accessible) return 0.6;
    return 1;
  }};

  &:hover {
    border-color: ${(props) => {
      if (props.isCurrentRoom) return 'var(--clr-green)';
      if (!props.accessible) return 'rgba(128, 128, 128, 0.7)';
      return 'var(--clr-purple)';
    }};
    transform: ${(props) =>
      props.isCurrentRoom || !props.accessible ? 'none' : 'translateY(-2px)'};
  }

  &:active {
    transform: ${(props) => (props.isCurrentRoom || !props.accessible ? 'none' : 'translateY(0)')};
  }

  ${(props) =>
    props.isCurrentRoom &&
    `
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
  `}

  ${(props) =>
    !props.accessible &&
    `
    background-color: rgba(128, 128, 128, 0.05);
    filter: grayscale(0.5);
  `}
`;

const RoomHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  gap: 1rem;
`;

const LockIcon = styled(Lock)`
  width: 1.2rem;
  height: 1.2rem;
  color: #888888;
  margin-left: 0.5rem;
`;

const ArchiveIcon = styled(Archive)`
  width: 1rem;
  height: 1rem;
  color: #00bfff;
  margin-left: 0.5rem;
  opacity: 0.9;
`;

const AgeRestrictionText = styled.span`
  color: #888888;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 0.5rem;
  display: block;
`;

const RoomTitle = styled.h3`
  color: var(--clr-sec);
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
  flex: 1;
`;

const UserCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--clr-sec);
  font-size: 0.9rem;
  opacity: 0.8;
`;

const UsersIcon = styled(Users)`
  width: 1rem;
  height: 1rem;
`;

const RoomDescription = styled.p`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.7;
`;

const RecentActiveUsers = styled.div`
  color: var(--clr-sec);
  font-size: 0.75rem;
  margin: 0.5rem 0 0 0;
  opacity: 0.8;
  line-height: 1.3;
`;

const ActiveUserTag = styled.span`
  color: ${props => props.userColor || 'var(--clr-sec)'};
  font-size: 0.75rem;
  font-weight: 600;
  
  &:not(:last-child)::after {
    content: ', ';
    color: var(--clr-sec);
    opacity: 0.6;
  }
`;

const CurrentRoomIndicator = styled.span`
  background-color: var(--clr-green);
  color: white;
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  /* Desktop: position in top-right corner, hide mobile version */
  @media ${device.tablet} {
    &.desktop-indicator {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
    }
    &.mobile-indicator {
      display: none;
    }
  }
  
  /* Mobile: hide desktop version, show mobile version below content */
  @media (max-width: 767px) {
    &.desktop-indicator {
      display: none;
    }
    &.mobile-indicator {
      display: inline-block;
      margin-top: 0.5rem;
      align-self: flex-start;
    }
  }
`;

const ArchiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.15) 0%, rgba(0, 150, 255, 0.1) 100%);
  color: #00bfff;
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  font-weight: 500;
  border: 1px solid rgba(0, 191, 255, 0.3);
  margin-top: 0.5rem;
  
  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const LoadingMessage = styled.p`
  color: var(--clr-sec);
  text-align: center;
  margin: 2rem 0;
  font-style: italic;
  opacity: 0.8;
`;

const ErrorMessage = styled.p`
  color: #ff8a8a;
  font-size: 0.9rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff6b6b;
  text-align: center;
`;

const UnderageBanBanner = styled.div`
  background: rgba(255, 140, 0, 0.12);
  border: 1px solid rgba(255, 140, 0, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  color: #ff8c00;
  font-size: 0.85rem;
  line-height: 1.4;

  a {
    color: #ff8c00;
    font-weight: 600;
    text-decoration: underline;
  }
`;

const RoomSelectionModal = ({
  visible,
  onClose,
  onRoomSelect,
  currentRoom,
  allowClose = true,
}) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userHasAge, setUserHasAge] = useState(false);
  const [hasUnderageBan, setHasUnderageBan] = useState(false);

  const renderRecentActiveUsers = (recentActiveUsers, recentAnonymousCount, lastMessageTime) => {
    const content = renderActiveUsersWithColors(recentActiveUsers, recentAnonymousCount, ActiveUserTag);
    if (!content) return null;

    const timeAgo = lastMessageTime ? dayjs(lastMessageTime).fromNow() : null;

    return (
      <RecentActiveUsers>
        Recently active: {content}
        {timeAgo && <span style={{ opacity: 0.6 }}> {timeAgo}</span>}
      </RecentActiveUsers>
    );
  };

  const fetchRooms = async () => {
    setLoading(true);
    setError('');

    try {
      // Get user's browser language
      const browserLanguage =
        navigator.language || navigator.userLanguage || 'en';

      // Try to include auth token if user is logged in
      const auth = getAuth();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          headers.Authorization = 'Bearer ' + token;
        } catch (tokenError) {
          console.warn('Failed to get auth token:', tokenError);
          // Continue without token
        }
      }

      const response = await fetch(
        API_URL + `rooms?language=${encodeURIComponent(browserLanguage)}`,
        {
          method: 'GET',
          headers,
        }
      );

      const result = await response.json();

      if (response.ok) {
        setUserHasAge(!!result.userHasAge);
        setHasUnderageBan(!!result.hasUnderageBan);
        let rooms = result.rooms || [];
        
        // Filter out rooms with "vent" in title/description if in ChatScape mode
        if (isChatScapeMode()) {
          rooms = rooms.filter(room => 
            !room.title.toLowerCase().includes('vent') &&
            !room.description.toLowerCase().includes('vent')
          );
        } else {
          // Add hardcoded Private Rage Room for VentScape users only
          const hardcodedRageRoom = {
            id: 'private-rage',
            title: 'Private Rage Room',
            description: 'Your personal venting space - completely offline & private',
            onlineUsers: 0,
            accessible: true,
            redirect_route: '/rage',
            isBeingArchived: false
          };
          
          rooms = [...rooms, hardcodedRageRoom];
        }
        
        setRooms(rooms);
      } else {
        setError(result.message || 'Failed to load rooms');
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchRooms();
    }
  }, [visible]);

  const handleRoomClick = (room) => {
    // Prevent clicking on the current room
    const isCurrentRoom = currentRoom && currentRoom.id === room.id;
    if (isCurrentRoom) {
      console.log(`Already in room ${room.id}, ignoring click`);
      return;
    }

    // Prevent clicking on inaccessible rooms
    if (!room.accessible) {
      if (room.blockedByUnderageBan) {
        setError('You have been restricted from age-restricted rooms. You can appeal this on our Discord server.');
      } else if (!room.meetsAgeRequirement && !room.meetsAccountRequirement) {
        setError('This room requires age verification and an account. Please set your birthday and sign in or create an account.');
      } else if (!room.meetsAccountRequirement) {
        setError('This room requires an account. Please sign in or create an account.');
      } else if (!room.meetsAgeRequirement && userHasAge) {
        setError('You do not meet the age requirements for this room.');
      } else {
        setError('This room requires age verification. Please set your age in your profile settings.');
      }
      return;
    }

    // Check if room has a redirect route (like rage room)
    if (room.redirect_route) {
      window.open(room.redirect_route, '_blank');
      onClose();
      return;
    }

    onRoomSelect(room);
    onClose();
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={allowClose ? onClose : () => {}}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          {allowClose && <CloseButton onClick={onClose} />}
          <ModernTitle>Select a Room</ModernTitle>
          <ModernSubtitle>
            {allowClose 
              ? "Choose a room to join the conversation" 
              : "Please select a room to get started with VentScape"}
          </ModernSubtitle>

          {loading && <LoadingMessage>Loading rooms...</LoadingMessage>}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {hasUnderageBan && (
            <UnderageBanBanner>
              Your access to age-restricted rooms has been restricted. If you believe this is an error, you can appeal on{' '}
              <a href="https://discord.gg/rM7g5Zusmr" target="_blank" rel="noopener noreferrer">Discord</a>.
            </UnderageBanBanner>
          )}

          {!loading && !error && (
            <RoomsContainer>
              {rooms.map((room) => {
                const isCurrentRoom = currentRoom && currentRoom.id === room.id;
                const activeCount = (room.recentActiveUsers?.length || 0) + (room.recentAnonymousCount || 0);
                return (
                  <RoomCard
                    key={room.id}
                    onClick={() => handleRoomClick(room)}
                    isCurrentRoom={isCurrentRoom}
                    accessible={room.accessible !== false}
                  >
                    {/* Desktop: show indicator at top for absolute positioning */}
                    {isCurrentRoom && (
                      <CurrentRoomIndicator className="desktop-indicator">
                        Currently Connected
                      </CurrentRoomIndicator>
                    )}
                    <RoomHeader>
                      <RoomTitle>
                        {room.title}
                        {room.isBeingArchived && <ArchiveIcon title="Messages are being archived" />}
                        {!room.accessible && <LockIcon />}
                      </RoomTitle>
                      {!room.redirect_route && (
                        <UserCount>
                          <UsersIcon />
                          {room.onlineUsers}
                          {activeCount > 0 && ` (${activeCount} active)`}
                        </UserCount>
                      )}
                    </RoomHeader>
                    <RoomDescription>{room.description}</RoomDescription>
                    {room.isBeingArchived && (
                      <ArchiveIndicator>
                        <Archive />
                        Messages in this room will be archived and viewable for 24 hours
                      </ArchiveIndicator>
                    )}
                    {!room.accessible && (
                      <AgeRestrictionText style={room.blockedByUnderageBan ? { color: '#ff8c00' } : undefined}>
                        {room.blockedByUnderageBan
                          ? 'Restricted \u2014 appeal on Discord'
                          : !room.meetsAgeRequirement && !room.meetsAccountRequirement
                            ? 'Age verification and account required'
                            : !room.meetsAccountRequirement
                              ? 'Account required'
                              : !room.meetsAgeRequirement && userHasAge
                                ? `Minimum age: ${room.minAge}`
                                : 'Age verification required'}
                      </AgeRestrictionText>
                    )}
                    {renderRecentActiveUsers(room.recentActiveUsers, room.recentAnonymousCount, room.lastMessageTime)}
                    {/* Mobile: show indicator at bottom in document flow */}
                    {isCurrentRoom && (
                      <CurrentRoomIndicator className="mobile-indicator">
                        Currently Connected
                      </CurrentRoomIndicator>
                    )}
                  </RoomCard>
                );
              })}
            </RoomsContainer>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default RoomSelectionModal;
